/**
 * @module app.CesiumLoader
 */
import appBase from './index.js';
import appOlcsControl from './OlcsControl.js';
import olcsOLCesium from 'ol-cesium/src/olcs/OLCesium.ts';
import appPoiLayers3d from './PoiLayers3d.js';
import appFeatureConverter from './FeatureConverter.js';
import appRasterSynchronizer, {addErrorWrapper} from './RasterSynchronizer.js';
import appVectorSynchronizer from './VectorSynchronizer.js';
import {MAP_PROJECTION, VECTOR_LAYERS} from './constants.js';
import {mapProjectionExtentToLonLat} from './utils.js';
import {containsXY} from "ol/extent";

const isLocalhost = document.location.hostname === 'localhost';

/**
 * @ngInject
 * @constructor
 * @struct
 *
 * @param {string} poiLayersDataUrl
 * @param {string} poiLayers3dImgUrl
 * @param {angular.$q} $q
 * @param {ngeo.statemanager.Location} ngeoLocation ngeo Location service.
 * @param {boolean} debugMode
 * @param {app.CacheBustService} appCacheBustService
 * @param {angular.$timeout} $timeout
 * @param {!ngeox.miscDebounce} ngeoDebounce
 * @param {!angular.Scope} $rootScope
 */
class CesiumLoader {
  constructor(
    poiLayersDataUrl,
    poiLayers3dImgUrl,
    $q,
    ngeoLocation,
    debugMode,
    appCacheBustService,
    $timeout,
    ngeoDebounce,
    $rootScope,
    appDetourService
  ) {
    /**
     * @type {angular.$q.Deferred<undefined>}
     * @private
     */
    this.loadingDefered_;

    /**
     * @private
     */
    this.cacheBustService_ = appCacheBustService;

    /**
     * @type {angular.$timeout}
     * @private
     */
    this.timeout_ = $timeout;

    /**
     * @const {!ngeox.miscDebounce}
     * @private
     */
    this.ngeoDebounce_ = ngeoDebounce;

    /**
     * @type {olcs.OLCesium}
     * @export
     */
    this.ol3d;

    /**
     * @type {app.OlcsControl}
     * @export
     */
    this.olcsControl;

    /**
     * @type {angular.$q}
     * @private
     */
    this.q_ = $q;

    /**
     * @type {angular.Scope}
     * @private
     */
    this.scope_ = null;

    /**
     * @type {ol.Map}
     * @private
     */
    this.map_;

    /**
     * @type {ngeo.statemanager.Location}
     * @private
     */
    this.ngeoLocation_ = ngeoLocation;

    const libPath = `lib${appCacheBustService.getVersion()}`;
    /**
     * @private
     * @type {string}
     */
    this.url_ = libPath + (debugMode ? '/CesiumUnminified/Cesium.js' : '/Cesium/Cesium.js');

    /**
     * @type {ol.Extent}
     * @private
     * This corresponds to the TileMaxtrixSet 4326 documented here:
     * http://api3.geo.admin.ch/services/sdiservices.html#terrain-service
     */
    this.terrainExtent_ = /** @type{!ol.Extent} */ (mapProjectionExtentToLonLat(MAP_PROJECTION.getExtent()));

    /**
     * @type {string}
     * @private
     */
    this.poiLayersDataUrl_ = poiLayersDataUrl;

    /**
     * @type {string}
     * @private
     */
    this.poiLayers3dImgUrl_ = poiLayers3dImgUrl;

    /**
     * @private
     */
    this.detourService_ = appDetourService;

    /**
     * @type {app.PoiLayers3d}
     */
    this.poiLayers3d;

    /**
     * @type {boolean}
     * @export
     */
    this.olcsEnabled = false;

    /**
     * @type {boolean}
     * @export
     */
    this.hide3dDisclaimer = false;

    /**
     * @type {Cesium.Primitive}
     */
    this.buildings3DTileset;

    /**
     * @type {Cesium.Primitive}
     */
    this.swissNames3DTileset;

    /**
     * @type {string}
     * @private
     */
    this.baseLayerId_;

    $rootScope.$on('base_layer_id_change', (evt, layerId) => {
      this.baseLayerId_ = layerId;
      if (this.ol3d) {
        this.update3DTiles_();
      }
    });

    /**
     * @param {string} layer
     * @return {boolean}
     */
    this.poi3dFilterFunction = function(layer) {
      return !VECTOR_LAYERS.includes(layer);
    };

    /**
     * @type {boolean}
     */
    this.cameraMoveStarted = false;

    /**
     * @type {undefined|function}
     */
    this.unlistenDetourUpdate = undefined;

    /**
     * @type {undefined|function}
     */
    this.unlistenDetourUpdateStart = undefined;
  }

  /**
   *
   * @param {!ol.Map} map
   * @param {!angular.Scope} scope
   * @param {!Object<string, boolean>} has
   * @param {!{layers: Object<string, boolean>, groups: Object<string, boolean>}} wmsCheckboxes
   * @export
   */
  initialize(map, scope, has, wmsCheckboxes) {
    this.map_ = map;
    this.scope_ = scope;

    /**
     * @private
     */
    this.wmsCheckboxes_ = wmsCheckboxes;

    if (has['localStorage']) {
      this.hide3dDisclaimer = window.localStorage.getItem('hide3dDisclaimer') === 'true';
    }

    if (this.ngeoLocation_.hasParam('3d_enabled')) {
      this.toggle3D();
    }

    this.scope_.$watch(() => this.hide3dDisclaimer, (value) => {
      if (has['localStorage'] && value !== undefined) {
        if (value) {
          window.localStorage.setItem('hide3dDisclaimer', 'true');
        } else {
          window.localStorage.removeItem('hide3dDisclaimer');
        }
      }
    });
  }

  /**
   * Display unsupported feature in 3D modal if 3D is active.
   * @return {boolean} true if app is not in 3D, otherwise false
   * @export
   */
  not3d() {
    if (this.is3dEnabled()) {
      $('#unsupported_feature_in_3d').modal('show');
      return false;
    }
    return true;
  }

  /**
   * @private
   * @param {string} url Script URL.
   * @param {function()} callback function to call on successful loading.
   */
  getScript_(url, callback) {
    const script = document.createElement('script');
    script.onload = callback;
    document.head.appendChild(script);
    script.src = url;
  }

  /**
   * @return {angular.$q.Promise<undefined>}
   * @private
   */
  load_() {
    if (!this.loadingDefered_) {
      // not yet loading
      this.loadingDefered_ = this.q_.defer();
      console.assert(!window['Cesium']);
      this.getScript_(this.url_, this.onload_.bind(this));
    }
    return this.loadingDefered_.promise;
  }

  /**
   * @private
   */
  onload_() {
    const ngeoLocation = this.ngeoLocation_;
    try {
      /**
       * @param {!ol.Map} map
       * @param {!Cesium.Scene} scene
       * @return {Array.<olcs.AbstractSynchronizer>}
       */
      const createSynchronizers = function(map, scene) {
        const featureConverter = new appFeatureConverter(scene, ngeoLocation);
        return [
          new appRasterSynchronizer(map, scene),
          new appVectorSynchronizer(map, scene, featureConverter)
        ];
      };

      console.assert(this.map_);
      const viewport = this.map_.getViewport();
      // to make shortlist visible on 3D map
      // search for overlay that contains shortlist and set zIndex to 1
      for (let i = 0; i < viewport.childNodes.length; i++) {
        const overlay = viewport.childNodes.item(i);
        if (overlay && overlay.querySelector('#shortlist')) {
          overlay.style.zIndex = 1;
          break;
        }
      }
      this.ol3d = new olcsOLCesium({
        map: this.map_,
        createSynchronizers: createSynchronizers
      });
    } catch (error) {
      console.error(error);
    }

    const scene = this.ol3d.getCesiumScene();
    scene.backgroundColor = Cesium.Color.WHITE;

    this.poiLayers3d = new appPoiLayers3d(scene, this.poiLayersDataUrl_, this.poiLayers3dImgUrl_, this.cacheBustService_, ngeoLocation);

    this.scope_.$watchCollection(() => this.wmsCheckboxes_.layers, this.update3DLayers_.bind(this));

    let minDistance = 300;
    if (ngeoLocation.hasParam('minDistance')) {
      minDistance = parseInt(ngeoLocation.getParam('minDistance'), 10);
    }
    const sscController = scene.screenSpaceCameraController;
    sscController.minimumZoomDistance = minDistance;
    sscController.maximumZoomDistance = 500000;

    // By default, disable the suspend terrain adjustment functionality
    // See https://github.com/AnalyticalGraphicsInc/cesium/issues/5999.
    Cesium.Camera.enableSuspendTerrainAdjustment = ngeoLocation.hasParam('suspendTerrainAdjustment');

    let maxTilt = appOlcsControl.MAX_TILT;
    if (ngeoLocation.hasParam('maxTilt')) {
      const degrees = parseInt(ngeoLocation.getParam('maxTilt'), 10);
      maxTilt = Cesium.Math.toRadians(degrees);
    }
    const camera = scene.camera;
    camera.constrainedAxisAngle = maxTilt;

    // Constrain the camera so that it stays in a 3D volume above Switzerland.
    // Near the ground the extent is small. Far from the ground the extent is larger.
    const limitCamera = function(extent) {
      const pos = camera.positionCartographic.clone();
      const inside = containsXY(extent, pos.longitude, pos.latitude);
      if (!inside) {
        // add a padding based on the camera height
        const padding = Math.max(0, pos.height * 0.05 / 500000);
        let lon = pos.longitude;
        let lat = pos.latitude;
        lon = Math.max(extent[0] - padding, lon);
        lat = Math.max(extent[1] - padding, lat);
        lon = Math.min(extent[2] + padding, lon);
        lat = Math.min(extent[3] + padding, lat);
        if (lon !== pos.longitude || lat !== pos.latitude) {
          pos.longitude = lon;
          pos.latitude = lat;
          camera.setView({
            destination: Cesium.Ellipsoid.WGS84.cartographicToCartesian(pos),
            orientation: {
              heading: camera.heading,
              pitch: camera.pitch
            }
          });
        }
      }
    };
    scene.postRender.addEventListener(
      limitCamera.bind(this, this.terrainExtent_.map(Cesium.Math.toRadians)), scene);

    const fog = scene.fog;
    fog.density = 0.0001;
    fog.screenSpaceErrorFactor = 25;

    const terrainProvider = new URLSearchParams(location.search).has('ionterrain') ?
      Cesium.createWorldTerrain() :
      new Cesium.CesiumTerrainProvider({
        // Layer.json: http://api3.geo.admin.ch/services/sdiservices.html#terrain-service
        // Multi domain configured from the layer.json.
        credit: 'Swisstopo terrain',
        url: isLocalhost ?
          'https://3d.geo.admin.ch/1.0.0/ch.swisstopo.terrain.3d/default/20200520/4326' :
          'https://terrain100.geo.admin.ch/1.0.0/ch.swisstopo.terrain.3d'
      });

    addErrorWrapper(terrainProvider);

    if (!ngeoLocation.hasParam('noterrain')) {
      scene.terrainProvider = terrainProvider;
    }

    scene.globe.depthTestAgainstTerrain = !ngeoLocation.hasParam('nodepthtest');
    this.ol3d.setEnabled(false);

    this.olcsEnabled = this.ol3d.getEnabled();

    this.olcsControl = new appOlcsControl(this.ol3d);

    // Exporting for easier debugging
    window['ol3d'] = this.ol3d;
    window['olcsControl'] = this.olcsControl;
    if (!ngeoLocation.hasParam('no3dtilesets')) {
      const unlisten = scene.globe.tileLoadProgressEvent.addEventListener(queueLength => {
        if (scene.globe.tilesLoaded) {
          this.create3DTileSets_();
          this.update3DTiles_();
          unlisten();
        }
      });
    }

    this.handleCesiumClickEvent();

    this.loadingDefered_.resolve();
  }

  /**
   * @private
   */
  create3DTileSets_() {
    // This constant is taken verbatim from geoadmin so that the styling is exactly the same.
    // We only quoted the json keys.
    // See https://github.com/geoadmin/mf-geoadmin3/blob/2affd1b45dc614d3ffbfe670d74ee5088e79647e/src/components/StylesService.js#L159.
    const labelStyleEnhanced = {
      'show': true,
      'labelStyle': 2,
      'labelText': '${DISPLAY_TEXT}',
      'disableDepthTestDistance': 5000,
      'anchorLineEnabled': true,
      'anchorLineColor': "color('white')",
      'heightOffset': {
        'conditions': [
          ['${LOD} === "7"', 20],
          ['${LOD} === "6"', 40],
          ['${LOD} === "5"', 60],
          ['${LOD} === "4"', 80],
          ['${LOD} === "3"', 100],
          ['${LOD} === "2"', 120],
          ['${LOD} === "1"', 150],
          ['${LOD} === "0"', 200],
          ['true', '200']
        ]
      },
      'labelColor': {
        'conditions': [
          ['${OBJEKTART} === "See"', 'color("blue")'],
          ['true', 'color("black")']
        ]
      },
      'labelOutlineColor': 'color("white", 1)',
      'labelOutlineWidth': 5,
      'font': {
        'conditions': [
          ['${OBJEKTART} === "See"', '"bold 32px arial"'],
          ['${OBJEKTART} === "Alpiner Gipfel"', '"italic 32px arial"'],
          ['true', '" 32px arial"']
        ]
      },
      'scaleByDistance': {
        'conditions': [
          ['${LOD} === "7"', 'vec4(1000, 1, 5000, 0.4)'],
          ['${LOD} === "6"', 'vec4(1000, 1, 5000, 0.4)'],
          ['${LOD} === "5"', 'vec4(1000, 1, 8000, 0.4)'],
          ['${LOD} === "4"', 'vec4(1000, 1, 10000, 0.4)'],
          ['${LOD} === "3"', 'vec4(1000, 1, 20000, 0.4)'],
          ['${LOD} === "2"', 'vec4(1000, 1, 30000, 0.4)'],
          ['${LOD} === "1"', 'vec4(1000, 1, 50000, 0.4)'],
          ['${LOD} === "0"', 'vec4(1000, 1, 500000, 0.4)'],
          ['true', 'vec4(1000, 1, 10000, 0.4)']
        ]
      },
      'translucencyByDistance': {
        'conditions': [
          ['${LOD} === "7"', 'vec4(5000, 1, 5001, 1)'],
          ['${LOD} === "6"', 'vec4(5000, 1, 5001, 1)'],
          ['${LOD} === "5"', 'vec4(5000, 1, 8000, 0.4)'],
          ['${LOD} === "4"', 'vec4(5000, 1, 10000, 0.4)'],
          ['${LOD} === "3"', 'vec4(5000, 1, 20000, 0.4)'],
          ['${LOD} === "2"', 'vec4(5000, 1, 30000, 0.4)'],
          ['${LOD} === "1"', 'vec4(5000, 1, 50000, 0.4)'],
          ['${LOD} === "0"', 'vec4(5000, 1, 500000, 1)'],
          ['true', 'vec4(5000, 1, 10000, 0.5)']
        ]
      },
      'distanceDisplayCondition': {
        'conditions': [
          ['${LOD} === "7"', 'vec2(0, 5000)'],
          ['${LOD} === "6"', 'vec2(0, 5000)'],
          ['${LOD} === "5"', 'vec2(0, 8000)'],
          ['${LOD} === "4"', 'vec2(0, 10000)'],
          ['${LOD} === "3"', 'vec2(0, 20000)'],
          ['${LOD} === "2"', 'vec2(0, 30000)'],
          ['${LOD} === "1"', 'vec2(0, 50000)'],
          ['${LOD} === "0"', 'vec2(0, 500000)'],
          ['${OBJEKTART} === "Alpiner Gipfel"', 'vec2(0, 100000)']
        ]
      }
    };

    const swisstopoTilesetPrefix = isLocalhost ?
      'https://vectortiles0.geo.admin.ch/3d-tiles' :
      'https://vectortiles100.geo.admin.ch/3d-tiles';
    const buildings3DTileset = (function() {
      const tileset = new Cesium.Cesium3DTileset({
        url: swisstopoTilesetPrefix + '/ch.swisstopo.swisstlm3d.3d/20190313/tileset.json',
        maximumNumberOfLoadedTiles: 3
      });
      tileset.show = false;
      return tileset;
    })();

    const swissNames3DTileset = (function createSwissNames() {
      const tileset = new Cesium.Cesium3DTileset({
        url: swisstopoTilesetPrefix + '/ch.swisstopo.swissnames3d.3d/20180716/tileset.json',
        maximumNumberOfLoadedTiles: 3
      });
      tileset.style = new Cesium.Cesium3DTileStyle(labelStyleEnhanced);
      tileset.show = false;
      return tileset;
    })();

    const scene = this.ol3d.getCesiumScene();
    scene.primitives.add(this.buildings3DTileset = buildings3DTileset);
    scene.primitives.add(this.swissNames3DTileset = swissNames3DTileset);
  }

  /**
   * @param {Cesium.Camera} camera
   * @private
   */
  cameraFromPermalink_(camera) {
    const lon = this.ngeoLocation_.getParamAsFloat('3d_lon');
    const lat = this.ngeoLocation_.getParamAsFloat('3d_lat');
    const elevation = this.ngeoLocation_.getParamAsInt('3d_elevation');
    const destination = Cesium.Cartesian3.fromDegrees(lon, lat, elevation);

    const heading = Cesium.Math.toRadians(this.ngeoLocation_.getParamAsFloat('3d_heading') || 0);
    const pitch = Cesium.Math.toRadians(this.ngeoLocation_.getParamAsFloat('3d_pitch') || 0);
    const roll = 0;
    const orientation = {heading, pitch, roll};

    camera.setView({
      destination,
      orientation
    });
  }

  /**
   * @param {Cesium.Camera} camera
   * @private
   */
  setupPermalink_(camera) {
    camera.moveEnd.addEventListener(this.ngeoDebounce_(() => {
      if (this.olcsEnabled) {
        const position = camera.positionCartographic;
        this.ngeoLocation_.updateParams({
          '3d_enabled': true,
          '3d_lon': Cesium.Math.toDegrees(position.longitude).toFixed(5),
          '3d_lat': Cesium.Math.toDegrees(position.latitude).toFixed(5),
          '3d_elevation': position.height.toFixed(0),
          '3d_heading': Cesium.Math.toDegrees(camera.heading).toFixed(3),
          '3d_pitch': Cesium.Math.toDegrees(camera.pitch).toFixed(3)
        });
      }
    }, 1000, true));
  }

  /**
   * @private
   */
  teardownPermalink_() {
    this.ngeoLocation_.getParamKeysWithPrefix('3d_').forEach((key) => {
      this.ngeoLocation_.deleteParam(key);
    });
  }

  /**
   * @export
   */
  toggle3D() {
    return this.load_().then(this.toggle3D_.bind(this));
  }

  /**
   * @private
   */
  toggle3D_() {
    if (!this.ol3d || !this.olcsControl) {
      // cesium is not active (no webgl / small screen)
      // webgl not enabled
      $('#webglDisabled').modal('show');
      // webgl not working; don't update the base layer.
      return;
    }

    const is3DCurrentlyEnabled = this.olcsEnabled;
    if (is3DCurrentlyEnabled) {
      // Disable 3D
      this.olcsControl.resetToNorthZenith(function() {
        this.olcsEnabled = false;
        this.ol3d.setEnabled(false);
        // Fix eventual rotation discrepency at end of transition
        this.normalizeView_();
      }.bind(this));
      this.teardownPermalink_();
      if (this.unlistenDetourUpdate) {
        this.unlistenDetourUpdate();
        this.unlistenDetourUpdateStart();
        this.unlistenDetourUpdate = undefined;
      }
    } else {
      // Enable 3D
      this.olcsEnabled = true;
      this.ol3d.setEnabled(true);
      const camera = this.ol3d.getCesiumScene().camera;
      if (this.ngeoLocation_.hasParam('3d_enabled')) {
        this.cameraFromPermalink_(camera);
      } else {
        this.olcsControl.tiltOnGlobe(Cesium.Math.toRadians(50));
      }
      this.setupPermalink_(camera);
      this.update3DLayers_(this.wmsCheckboxes_.layers);
      if (!this.hide3dDisclaimer) {
        const disclaimer = $('#3dDisclaimer').modal('show');
        disclaimer.on('shown.bs.modal', () => {
          this.timeout_(() => {
            disclaimer.modal('hide');
          }, 15000);
        });
      }
      if (!this.unlistenDetourUpdate) {
        this.unlistenDetourUpdateStart = camera.moveStart.addEventListener(() => {
          // required because of moveEnd sometimes triggers few times
          this.cameraMoveStarted = true;
        });
        const scratchPoint1 = new Cesium.Cartographic();
        const scratchPoint2 = new Cesium.Cartographic();
        this.unlistenDetourUpdate = camera.moveEnd.addEventListener(() => {
          if (this.cameraMoveStarted) {
            const viewRect = camera.computeViewRectangle(this.ol3d.getCesiumScene().globe.ellipsoid);
            this.detourService_.getVisibleFeatures().then(detours => {
              detours = detours.filter(feature => {
                const extent = mapProjectionExtentToLonLat(feature.getGeometry().getExtent());
                const point1 = Cesium.Cartographic.fromDegrees(extent[0], extent[1], scratchPoint1);
                const point2 = Cesium.Cartographic.fromDegrees(extent[2], extent[3], scratchPoint2);
                const point1InView = Cesium.Rectangle.contains(viewRect, point1);
                const point2InView = Cesium.Rectangle.contains(viewRect, point2);
                return point1InView || point2InView;
              });
              this.detourService_.updateFeatures_(detours);
            });
            this.cameraMoveStarted = false;
          }
        });
      }
    }
  }

  /**
   * @param {!Object.<string, boolean>} layers
   * @private
   */
  update3DLayers_(layers) {
    if (!this.ol3d || !this.ol3d.getEnabled()) {
      return;
    }
    for (const code in layers) {
      if (this.poi3dFilterFunction(code)) {
        // no 3D cluster POI available
        continue;
      }
      const isActive = layers[code];
      this.poiLayers3d.updateLayer(code, isActive);
    }
  }

  /**
   * @private
   */
  normalizeView_() {
    const view = this.map_.getView();
    const resolution = view.getResolution();
    view.setRotation(0);
    view.setResolution(view.getConstrainedResolution(resolution));
  }

  /**
   * @return {boolean}
   */
  is3dEnabled() {
    return !!this.ol3d && this.ol3d.getEnabled();
  }

  /**
   * @private
   */
  update3DTiles_() {
    if (this.baseLayerId_ && this.buildings3DTileset) {
      const show3DTiles = this.baseLayerId_ != 'lb'; // not Swissimage
      this.buildings3DTileset.show = show3DTiles;
      this.swissNames3DTileset.show = show3DTiles;
    }
  }

  handleCesiumClickEvent() {
    const eventHandler = new Cesium.ScreenSpaceEventHandler(this.ol3d.getCesiumScene().canvas);

    eventHandler.setInputAction((e) => {
      const ray = this.ol3d.getCesiumScene().camera.getPickRay(e.position);
      const cartesian = this.ol3d.getCesiumScene().globe.pick(ray, this.ol3d.getCesiumScene());
      this.map_.dispatchEvent(
        new jQuery.Event(
          {
            target: e.target,
            type: 'singleclick'
          },
          {
            cartesian,
            pixel: [e.position.x, e.position.y]
          }
        )
      );
    },
    Cesium.ScreenSpaceEventType['LEFT_CLICK']);
  }


}

appBase.service('appCesiumLoader', CesiumLoader);


export default CesiumLoader;
